/**
 * `BiSelect` — drop-in dark + orange replacement for the native `<select>`.
 *
 * Same API surface as `<select>` (value + onChange event + children `<option>`
 * elements) so migration across the app is a mechanical tag swap. The dropdown
 * uses Radix Popover under the hood and matches the JobPickerSheet visual
 * language: dark #1F1F1F surface, quiet grey rows, soft-orange fill on the
 * selected row with an orange checkmark on the right.
 *
 * Usage:
 *   <BiSelect value={x} onChange={(e) => setX(e.target.value)} className="w-full bi-input">
 *     <option value="a">Alpha</option>
 *     <option value="b">Bravo</option>
 *   </BiSelect>
 *
 * Notes:
 *   - Placeholder-style options (`value=""`) are supported (unlike Radix Select).
 *   - `disabled` on options and the field itself is respected.
 *   - The trigger keeps whatever `data-testid` was on the original `<select>`;
 *     each row gets `data-testid={testIdPrefix}-option-<value>` for e2e anchors.
 */
import { useState, useMemo, Children } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

function flatten(children, out) {
  Children.forEach(children, (child) => {
    if (!child || typeof child !== "object") return;
    if (child.type === "option") {
      const val =
        child.props.value !== undefined
          ? String(child.props.value)
          : String(child.props.children ?? "");
      out.push({
        value: val,
        label: child.props.children,
        disabled: !!child.props.disabled,
      });
    } else if (child.props && child.props.children) {
      // Skip optgroup wrappers — flatten their options.
      flatten(child.props.children, out);
    }
  });
}

export default function BiSelect({
  value,
  onChange,
  className,
  disabled,
  children,
  placeholder,
  "data-testid": testId,
  "aria-label": ariaLabel,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const options = useMemo(() => {
    const arr = [];
    flatten(children, arr);
    return arr;
  }, [children]);

  const selected = options.find((o) => o.value === String(value ?? ""));
  const label =
    selected?.label ?? placeholder ?? (options[0]?.label ?? "\u00A0");

  const pick = (v) => {
    setOpen(false);
    // Mimic native onChange event shape so callers using
    // `(e) => setX(e.target.value)` keep working unchanged.
    onChange?.({ target: { value: v } });
  };

  const triggerTestId = testId ?? undefined;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={triggerTestId}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            "bi-input inline-flex items-center justify-between gap-2 text-left disabled:opacity-60 disabled:cursor-not-allowed",
            className,
          )}
          {...rest}
        >
          <span className="truncate flex-1 min-w-0">{label}</span>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-white/60 shrink-0 transition-transform duration-150",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-1 bg-[#1F1F1F] border border-white/10 text-white rounded-xl shadow-[0_20px_50px_-10px_rgba(0,0,0,0.7)] w-[var(--radix-popover-trigger-width)] min-w-[10rem] max-h-[60vh] overflow-y-auto"
      >
        {options.length === 0 && (
          <div className="px-3 py-2 text-sm text-white/40 text-center">
            No options
          </div>
        )}
        {options.map((o) => {
          const active = String(value ?? "") === o.value;
          return (
            <button
              key={o.value}
              type="button"
              disabled={o.disabled}
              data-testid={
                triggerTestId ? `${triggerTestId}-option-${o.value}` : undefined
              }
              onClick={() => pick(o.value)}
              className={cn(
                "w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center justify-between gap-3 transition disabled:opacity-40 disabled:cursor-not-allowed",
                active
                  ? "bg-[rgba(255,149,0,0.15)] border border-[#FF9500]/40 text-white"
                  : "border border-transparent text-white/85 hover:bg-white/[0.06]",
              )}
            >
              <span className="truncate">{o.label}</span>
              {active && (
                <Check className="w-4 h-4 text-[#FF9500] shrink-0" />
              )}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
